import { Button, Card, Collapse, Descriptions, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import {
	baytFormat,
	kindLabel,
	nomzodXulosasi,
	type RetentionRun,
	STATUS_RANGI,
	statusLabel,
} from "../types";

const { Text } = Typography;

interface RunCardProps {
	run: RetentionRun;
	onConfirm: (run: RetentionRun) => void;
	onCancel: (run: RetentionRun) => void;
}

/** `details` dagi nomzodlar ro'yxati — tur bo'yicha maydon nomi farq qiladi. */
function nomzodRoyxati(run: RetentionRun): string[] {
	return run.details?.partitions ?? run.details?.namuna ?? [];
}

/**
 * Ochiq yugurish kartasi.
 *
 * ⚠️⚠️ **Dry-run kartasi OGOHLANTIRISH rangida (sariq), XAVF rangida
 * (qizil) EMAS.** Bu hali hech narsa o'chirilmagan holat va qizil
 * «allaqachon kech» degan taassurot berardi — admin shoshib qaror
 * qabul qilardi. Qizil faqat modaldagi «qaytarib bo'lmaydi»
 * ogohlantirishida va tugmada.
 *
 * ⚠️ Nomzodlar soni va **bo'shatiladigan hajm** aniq ko'rsatiladi —
 * admin nima tasdiqlayotganini bilishi kerak. Manba modal bilan bir
 * xil (`nomzodXulosasi`).
 *
 * ⚠️ Tafsilot ochilganda partition nomlari yoki fayl namunasi
 * ko'rsatiladi. Ro'yxat QISQARTIRILGAN bo'lishi mumkin (`checkin_photos`
 * da `details` ga faqat 20 ta id yoziladi) va bu ANIQ aytiladi —
 * «jimgina chegara» yaramaydi.
 */
export function RunCard({ run, onConfirm, onCancel }: RunCardProps) {
	const { t } = useTranslation("retention");
	const royxat = nomzodRoyxati(run);
	const qisqargan = royxat.length > 0 && royxat.length < run.candidateCount;

	/**
	 * ⚠️⚠️ **Ikki tugmaning sharti AJRALGAN va bu ataylab.**
	 * Tasdiqlash faqat `dry_run` da — backend `confirm` ni AYNAN shu
	 * holat bilan chegaralaydi (`retention.repository.ts`), ya'ni
	 * `confirmed` kartada tugma ko'rsatilsa bosilishi 422 bilan
	 * tugardi. Bekor qilish esa `confirmed` da HAM ochiq: supuruvchi
	 * kunlik, ya'ni xato tasdiqlangan yugurishni to'xtatish oynasi
	 * ~24 soat va u yagona chiqish yo'li. `running` da ikkalasi ham
	 * yopiq — o'chirish allaqachon ketgan.
	 */
	const tasdiqlanadi = run.status === "dry_run";
	const bekorQilinadi = run.status === "dry_run" || run.status === "confirmed";

	return (
		<Card
			size="small"
			title={
				<Space>
					<Text strong>{kindLabel(t, run.kind)}</Text>
					<Tag color={STATUS_RANGI[run.status]}>{statusLabel(t, run.status)}</Tag>
				</Space>
			}
			extra={
				bekorQilinadi ? (
					<PermissionGate resource="retention" action="manage">
						<Space>
							<Button size="small" onClick={() => onCancel(run)}>
								{t("runCard.cancel")}
							</Button>
							{tasdiqlanadi ? (
								<Button size="small" danger type="primary" onClick={() => onConfirm(run)}>
									{t("runCard.confirm")}
								</Button>
							) : null}
						</Space>
					</PermissionGate>
				) : null
			}
		>
			<Descriptions size="small" column={1}>
				<Descriptions.Item label={t("runCard.deletesLabel")}>
					<Text strong>{nomzodXulosasi(t, run)}</Text>
				</Descriptions.Item>
				<Descriptions.Item label={t("runCard.cutoffLabel")}>
					{new Date(run.cutoffAt).toLocaleString("uz-UZ")}
				</Descriptions.Item>
				<Descriptions.Item label={t("runCard.computedLabel")}>
					{new Date(run.createdAt).toLocaleString("uz-UZ")}
				</Descriptions.Item>
				{run.reason ? (
					<Descriptions.Item label={t("runCard.reasonLabel")}>{run.reason}</Descriptions.Item>
				) : null}
			</Descriptions>

			{royxat.length > 0 ? (
				<Collapse
					ghost
					size="small"
					items={[
						{
							key: "tafsilot",
							label: t("runCard.detailsLabel", {
								count: royxat.length,
								sample: qisqargan ? t("runCard.detailsSampleSuffix") : "",
							}),
							children: (
								<>
									{qisqargan ? (
										<Text type="secondary" className="!text-xs">
											{t("runCard.truncatedNotice", { total: run.candidateCount })}
										</Text>
									) : null}
									<ul className="!mb-0 !pl-5">
										{royxat.map((element) => (
											<li key={element}>
												<Text code>{element}</Text>
											</li>
										))}
									</ul>
								</>
							),
						},
					]}
				/>
			) : null}

			{run.status === "confirmed" ? (
				<Text type="secondary" className="!text-xs">
					{t("runCard.confirmedNotice", { bytes: baytFormat(run.details?.bytes) })}
				</Text>
			) : null}
		</Card>
	);
}
